import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, useToast, Text, Table, Thead, Tbody, Tr, Th, Td, Button,
  HStack,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ThreeDotMenu from '../components/ThreeDotMenu';
import { getplayerTicketData, getTicketDetails } from '../actions/playerTicketAction';
import './TicketDetails.css';
import salesReportTableStyles from '../styles/SalesReportTable.module.css';
import '../utils/api';

const CardContainer = styled.div`
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  margin: 20px auto 30px;
  padding: 20px;
  width: 100%;
  background: ${({ $red }) => ($red ? 'red' : '#fff')};
  color: ${({ $red }) => ($red ? '#fff' : '#000')};
`;

const TicketDetailsNew = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const [userID, setUserID] = useState(null);
  const [FDate, setFDate] = useState(null);
  const [TDate, setTDate] = useState(null);
  const [ticketId, setTicketID] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { ticketListData, loading, error } = useSelector((state) => state.playerTicket);
  const { ticketDetails } = useSelector((state) => state.playerTicketDetails);

  const toFormattedDateParts = (dateString) => {
    if (!dateString) return null;

    const normalized = String(dateString).trim().replace('T', ' ').replace('Z', '');
    const match = normalized.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );

    if (match) {
      return {
        year: match[1],
        month: match[2],
        day: match[3],
        hour: match[4] || '00',
        minute: match[5] || '00',
        second: match[6] || '00',
      };
    }

    const parsedDate = new Date(dateString);
    if (Number.isNaN(parsedDate.getTime())) return null;

    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const partsMap = formatter
      .formatToParts(parsedDate)
      .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});

    return {
      year: partsMap.year,
      month: partsMap.month,
      day: partsMap.day,
      hour: partsMap.hour,
      minute: partsMap.minute,
      second: partsMap.second,
    };
  };

  const formatBookingDateTime = (dateString) => {
    const parts = toFormattedDateParts(dateString);
    if (!parts) return '-';

    return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute}:${parts.second}`;
  };

  const formatDrawDateTime = (dateString) => {
    const parts = toFormattedDateParts(dateString);
    if (!parts) return '-';

    return `${parts.day}-${parts.month}-${parts.year} ${parts.hour}:${parts.minute}`;
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const userID = queryParams.get('UserID');
    const fDate = queryParams.get('FDate');
    const tDate = queryParams.get('TDate');
    const ticketId = queryParams.get('TicketID');

    setUserID(userID);
    setFDate(fDate);
    setTDate(tDate);
    setTicketID(ticketId);

    if (userID && fDate && tDate) {
      dispatch(getplayerTicketData(fDate, tDate, userID)).catch((err) =>
        toast({
          title: 'Error fetching ticket list',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      );
    }
  }, [dispatch, location.search, toast]);

  useEffect(() => {
    setCurrentPage(1);
  }, [userID, FDate, TDate, ticketId]);

  useEffect(() => {
    if (ticketId && ticketId !== "0" && (!ticketDetails || Object.keys(ticketDetails).length === 0)) {
      dispatch(getTicketDetails(ticketId)).catch((err) =>
        toast({
          title: 'Error fetching ticket details',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      );
    }
  }, [dispatch, ticketId, ticketDetails, toast]);

  if (loading || !ticketListData) return <Box>Loading...</Box>;
  if (error) return <Box>Error: {error}</Box>;

  const rowsPerPage = 10;
  const totalRows = Array.isArray(ticketListData) ? ticketListData.length : 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const paginatedTickets = (ticketListData || []).slice(startIndex, startIndex + rowsPerPage);

  // Case: TicketID not selected
  if (!ticketId || ticketId === "0") {
    return (
      <Box className="ticket-container">
        <CardContainer $red={true}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap">
            <Text fontSize="lg" fontWeight="bold">
              TR 0  Sale Report for <u>{userID}</u> ({FDate} to {TDate})
            </Text>
            <HStack spacing={2}>
              <ThreeDotMenu />
            </HStack>
          </Box>
        </CardContainer>

        <Box overflowX="auto" className={salesReportTableStyles.tableWrapper}>
          <Table variant="striped" size="sm" className={salesReportTableStyles.table}>
            <Thead>
              <Tr>
                <Th>TicketID</Th>
                <Th>Draw</Th>
                <Th>Booking</Th>
                <Th>Draw</Th>
                <Th>Bet</Th>
                <Th>Win</Th>
                <Th>Claimed</Th>
                <Th>Result</Th>
                <Th>Points</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedTickets.length === 0 ? (
                <Tr>
                  <Td colSpan={9} textAlign="center">No tickets found for this period.</Td>
                </Tr>
              ) : (
                paginatedTickets.map((ticket, index) => (
                <Tr key={`${ticket.ticket_id}-${index}`}>
                  <Td>
                    <a href={`?UserID=${userID}&FDate=${FDate}&TDate=${TDate}&TicketID=${ticket.ticket_id}`} className="ticket-link">
                      {ticket.ticket_id}
                    </a>
                  </Td>
                  <Td>{ticket.draw_id}</Td>
                  <Td>{formatBookingDateTime(ticket.entry_time)}</Td>
                  <Td>{formatDrawDateTime(ticket.draw_time)}</Td>
                  <Td>{ticket.total_points}</Td>
                  <Td>{ticket.win_points || '-'}</Td>
                  <Td>{ticket.status === 'claimed' ? '✓' : '👎'}</Td>
                  <Td>
                    {ticket.result} <span className="badge">{ticket.bonus}</span>
                  </Td>
                  <Td>{ticket.points}</Td>
                </Tr>
              )))}
            </Tbody>
          </Table>
        </Box>
        <Box
          className="page-buttons"
          mt={4}
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <Button
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            isDisabled={safeCurrentPage === 1}
          >
            Prev
          </Button>

          {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNo) => (
            <Button
              key={pageNo}
              size="sm"
              onClick={() => setCurrentPage(pageNo)}
              colorScheme={safeCurrentPage === pageNo ? 'red' : 'gray'}
              variant={safeCurrentPage === pageNo ? 'solid' : 'outline'}
            >
              {pageNo}
            </Button>
          ))}

          <Button
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            isDisabled={safeCurrentPage === totalPages}
          >
            Next
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <CardContainer $red>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Text fontSize="xl" fontWeight="bold">Ticket Details</Text>
            <Text fontSize="2xl" fontWeight="bold">Ticket ID: {ticketId}</Text>
          </Box>
          <HStack>
            <Button
              size="sm"
              colorScheme="whiteAlpha"
              variant="solid"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </HStack>
        </Box>
      </CardContainer>

      {ticketDetails && Object.keys(ticketDetails).length > 0 ? (
        <>
          <CardContainer>
            <Text><b>Player:</b> {ticketDetails.player_id}</Text>
            <Text><b>Draw:</b> {ticketDetails.game_id}</Text>
            <Text><b>Draw Time:</b>  {formatDrawDateTime(ticketDetails?.draw_time)}</Text>
            <Text><b>Booking Time:</b> {formatBookingDateTime(ticketDetails?.entry_time)}  </Text>
            <Text><b>Status:</b> {ticketDetails.status}</Text>

            <Text fontWeight="bold" mt={3}>Bet Details:</Text>
            {(ticketDetails?.bet_details || []).map((bet, i) => (
              <Text key={i}>
                <span className="label label-info">{bet.position}</span> → Qty: {bet.points}
              </Text>
            ))}
          </CardContainer>

          <CardContainer>
            <Text><b>Total Points:</b> {ticketDetails.total_points}</Text>
            {ticketDetails?.win_points > 0 ? (
              <Box bg="red.100" border="1px solid red" borderRadius="md" p={2} mt={3} display="inline-block">
                <Text color="red.800" fontWeight="bold">
                  Win Points: {ticketDetails.win_points}
                </Text>
              </Box>
            ) : (
              <Text><b>Win Points:</b> {ticketDetails?.win_points}</Text>
            )}
            <br/>
            <Box bg="red.100" border="1px solid red" borderRadius="md" p={2} mt={3} display="inline-block">
              <Text color="red.800" fontWeight="bold">
                Win Position: {ticketDetails.win_position} ★ {ticketDetails.bonus}
              </Text>
            </Box>
          </CardContainer>
        </>
      ) : (
        <CardContainer>
          <Text color="red.500" fontWeight="bold">Ticket Details Not Available</Text>
        </CardContainer>
      )}
    </Box>
  );
};

export default TicketDetailsNew;
